import { useState } from 'react';
import { T, useGT } from 'gt-react';
import {
  ArrowsClockwiseIcon,
  DotsThreeVerticalIcon,
  TrashIcon,
} from '@phosphor-icons/react';
import { Button } from 'src/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from 'src/components/ui/dropdown-menu';
import { SendTaskBackToAgentDialog } from './SendTaskBackToAgentDialog';
import { useSendBackToAgentPlan } from './useSendBackToAgentPlan';
import type {
  SendBackTaskSnapshot,
  SendBackTaskTypeSnapshot,
} from './sendBackToAgentPlan';
import { DeleteTaskDialog } from './TaskDeleteControls';
import { useCanDeleteTask, type DeletableTask } from './taskDeletion';

type HeaderMenuDialog = 'send-back' | 'delete';

/**
 * The task header's `⋮` overflow menu. The trigger renders only when at least
 * one of its actions applies. The dialogs sit outside the menu so they outlive
 * it, and the delete dialog stays mounted even once the refetched task is
 * deleted (and `canDelete` turns false), so it can finish closing.
 */
export function TaskHeaderMenu({
  task,
  taskType,
  agents,
}: {
  task: SendBackTaskSnapshot & DeletableTask & { id: string };
  taskType: SendBackTaskTypeSnapshot | null | undefined;
  agents: Array<{ id: string; name: string }>;
}) {
  const gt = useGT();
  const [dialog, setDialog] = useState<HeaderMenuDialog | null>(null);
  const sendBackPlan = useSendBackToAgentPlan(task, taskType);
  const canDelete = useCanDeleteTask(task);
  const hasActions = sendBackPlan !== null || canDelete;

  function dialogOpenChange(which: HeaderMenuDialog) {
    return (open: boolean) => setDialog(open ? which : null);
  }

  return (
    <>
      {hasActions && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="icon"
              data-testid="task-overflow-menu"
              aria-label={gt('More task actions', {
                context: 'accessible label for the task header overflow menu',
              })}
            >
              <DotsThreeVerticalIcon className="size-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {sendBackPlan && (
              <DropdownMenuItem
                data-testid="send-task-back-to-agent"
                onSelect={() => setDialog('send-back')}
              >
                <ArrowsClockwiseIcon className="size-4" />
                <T context="menu item on the task page that opens confirmation to re-queue the task for its task type's default agent">
                  Send Back to Agent
                </T>
              </DropdownMenuItem>
            )}
            {sendBackPlan && canDelete && <DropdownMenuSeparator />}
            {canDelete && (
              <DropdownMenuItem
                variant="destructive"
                data-testid="delete-task"
                onSelect={() => setDialog('delete')}
              >
                <TrashIcon className="size-4" />
                <T context="menu item that opens confirmation to delete the task">
                  Delete…
                </T>
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      {sendBackPlan && (
        <SendTaskBackToAgentDialog
          taskId={task.id}
          plan={sendBackPlan}
          agents={agents}
          open={dialog === 'send-back'}
          onOpenChange={dialogOpenChange('send-back')}
        />
      )}
      <DeleteTaskDialog
        taskId={task.id}
        open={dialog === 'delete'}
        onOpenChange={dialogOpenChange('delete')}
      />
    </>
  );
}
